import React, { useState, useEffect, useContext } from "react";
import { useTheme } from "@mui/material/styles";
import TreeView from "react-treeview";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { format } from "date-fns";
import es from "date-fns/locale/es";

import {
  FolderOpen as FolderOpenIcon,
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  WorkOutlineOutlined as WorkOutlineOutlinedIcon,
} from "@mui/icons-material";

import { Add } from "@mui/icons-material";

import {
  getMaletasTree,
  getMuestrasInMaleta,
} from "../../api/Maletas/maletasApi";

import DrawerNuevaMaleta from "./DrawerNuevaMaleta"; // Drawer para crear maletas
import DrawerEditarMaleta from "./DrawerEditarMaleta"; // Nuevo Drawer para editar maletas

import "react-treeview/react-treeview.css";

const Maletas = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isMinimized } = useContext(SidebarContext);
  const marginLeft = isMinimized ? "3em" : "3em";
  const { contentMargin } = useContext(SidebarContext);
  const [treeData, setTreeData] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedMaleta, setSelectedMaleta] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // 🔹 Estado para el buscador
  const [filteredTreeData, setFilteredTreeData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
    } else {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp < currentTime) {
          localStorage.removeItem("token");
          navigate("/login");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
        navigate("/login");
      }
    }

    fetchMaletas();
  }, [navigate]);

  const fetchMaletas = async () => {
    try {
      const data = await getMaletasTree();
      if (!data || !Array.isArray(data)) {
        setTreeData({});
      } else {
        const structuredData = transformTreeData(data);
        setTreeData(structuredData);
      }
    } catch (error) {
      console.error("Error al obtener el árbol de maletas:", error);
      setTreeData({});
    }
  };

  // 🔹 Función para estructurar los datos
  const transformTreeData = (maletas) => {
    return maletas.reduce((acc, maleta) => {
      const { ID_USUARIO, USERNAME, FECHA_CREACION } = maleta;

      if (!acc[ID_USUARIO]) {
        acc[ID_USUARIO] = {
          username: USERNAME || `Usuario ${ID_USUARIO}`,
          fechas: {},
        };
      }

      const fechaFormateada = format(new Date(FECHA_CREACION), "dd-MM-yyyy", {
        locale: es,
      });

      if (!acc[maleta.ID_USUARIO].fechas[fechaFormateada]) {
        acc[maleta.ID_USUARIO].fechas[fechaFormateada] = [];
      }

      acc[maleta.ID_USUARIO].fechas[fechaFormateada].push({
        id: maleta.ID_MALETA,
        nombre: maleta.NOMBRE,
        estado: maleta.ESTADO,
      });

      return acc;
    }, {});
  };

  const toggleNode = (nodeKey) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [nodeKey]: !prev[nodeKey],
    }));
  };

  const handleEditMaleta = async (maleta) => {
    try {
      const muestras = await getMuestrasInMaleta(maleta.id);
      setSelectedMaleta({
        ...maleta,
        muestras: muestras || [], // Asegurar que siempre haya un array
      });

      setIsEditDrawerOpen(true); // 🔹 Asegurar que el Drawer siempre se abre
    } catch (error) {
      console.error("❌ Error al obtener muestras de la maleta:", error);
      setSelectedMaleta({ ...maleta, muestras: [] }); // 🔹 Incluso si hay error, actualizar estado
      setIsEditDrawerOpen(true); // 🔹 Asegurar que el Drawer se abra en caso de error
    }
  };

  const handleSelectMaleta = async (maleta) => {
    try {
      const muestras = await getMuestrasInMaleta(maleta.id);

      setSelectedMaleta({
        ...maleta,
        muestras: muestras || [], // ✅ Siempre actualiza `selectedMaleta`
      });
    } catch (error) {
      console.error("❌ Error al obtener muestras de la maleta:", error);
      setSelectedMaleta({ ...maleta, muestras: [] }); // ✅ Incluso si hay error, actualiza el estado
    }
  };

  const handleMuestraAdded = async (maletaId) => {
    try {
      // 1️⃣ Recargar la lista completa de maletas
      await fetchMaletas();

      // 2️⃣ Si la maleta editada está seleccionada, actualizar su contenido sin perder el estado
      if (selectedMaleta && selectedMaleta.id === maletaId) {
        const muestrasActualizadas = await getMuestrasInMaleta(maletaId);
        setSelectedMaleta((prev) => ({
          ...prev,
          muestras: muestrasActualizadas || [],
        }));
      }
    } catch (error) {
      console.error("❌ Error al actualizar la maleta:", error);
    }
  };

  const handleMuestraRemoved = async (maletaId, isLast) => {
    try {
      // Recargar la lista de maletas
      await fetchMaletas();

      // Si la maleta eliminada estaba seleccionada y ya no tiene muestras, limpiar la lista pero NO cerrar el panel
      if (selectedMaleta && selectedMaleta.id === maletaId) {
        if (isLast) {
          setSelectedMaleta((prev) => ({
            ...prev,
            muestras: [], // En lugar de cerrar, dejamos la maleta seleccionada sin muestras
          }));
        } else {
          const muestras = await getMuestrasInMaleta(maletaId);
          setSelectedMaleta((prev) => ({
            ...prev,
            muestras: muestras || [],
          }));
        }
      }
    } catch (error) {
      console.error(
        "❌ Error al actualizar maletas después de eliminación:",
        error
      );
    }
  };

  // 🔍 **Función para filtrar el árbol con búsqueda avanzada**

  useEffect(() => {
    if (!searchTerm) {
      setFilteredTreeData(treeData);
      return;
    }

    const lowerTerm = searchTerm.toLowerCase();
    const filteredData = {};
    const newExpandedNodes = {};

    Object.entries(treeData || {}).forEach(([userId, user]) => {
      let userMatches = user.username.toLowerCase().includes(lowerTerm);
      let filteredFechas = {};

      Object.entries(user.fechas).forEach(([fecha, maletas]) => {
        let fechaMatches = fecha.includes(lowerTerm);
        let maletasFiltradas = maletas.filter((m) =>
          m.nombre.toLowerCase().includes(lowerTerm)
        );

        if (fechaMatches || maletasFiltradas.length > 0) {
          filteredFechas[fecha] =
            maletasFiltradas.length > 0 ? maletasFiltradas : maletas;
          newExpandedNodes[`${userId}-${fecha}`] = true; // Expandir la fecha si hay coincidencias
        }
      });

      if (userMatches || Object.keys(filteredFechas).length > 0) {
        filteredData[userId] = {
          username: user.username,
          fechas: filteredFechas,
        };
        newExpandedNodes[userId] = true; // Expandir el usuario si hay coincidencias
      }
    });

    setFilteredTreeData(filteredData);
    setExpandedNodes(newExpandedNodes);
  }, [searchTerm, treeData]);

  if (!treeData || Object.keys(treeData).length === 0) {
    return <Typography variant="h6">No hay maletas registradas</Typography>;
  }

  return (
    <Box sx={{ marginLeft, ...contentMargin, display: "flex", gap: 2, mt: 2 }}>
      <Box
        sx={{
          width: "350px",
          backgroundColor: theme.palette.background.paper,
          p: 2,
          borderRadius: "4px",
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        {" "}
        <TextField
          label="Buscar en el árbol..."
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Maletas registradas</Typography>

          <IconButton
            onClick={() => setIsDrawerOpen(true)}
            sx={{
              ml: 3,
              border: "1px solid  #90b2b7",
              backgroundColor: theme.palette.background.paper,
              color: " #90b2b7",
              padding: "5px",
              "&:hover": { backgroundColor: "#ffac7e", color: "#fff" },
            }}
          >
            <Add />
          </IconButton>
        </Box>
        <Divider sx={{ mt: 2, mb: 2 }} />
        {treeData && Object.keys(treeData).length > 0 ? (
          Object.entries(treeData).map(([userId, user]) => (
            <TreeView
              key={userId}
              nodeLabel={
                <div
                  onClick={() => toggleNode(userId)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    fontWeight: 800,
                    mt: 1,
                  }}
                >
                  <AccountTreeOutlinedIcon
                    sx={{
                      color: "#90b2b7",
                      marginRight: ".5em",
                      marginTop: ".5em",
                    }}
                  />
                  {user.username}
                </div>
              }
              collapsed={!expandedNodes[userId]}
            >
              {Object.entries(user.fechas).map(([fecha, maletas]) => (
                <TreeView
                  key={fecha}
                  nodeLabel={
                    <div
                      onClick={() => toggleNode(`${userId}-${fecha}`)}
                      style={{
                        display: "flex",
                        cursor: "pointer",
                        fontWeight: 700,
                        marginTop: ".5em",
                        marginLeft: ".5em",
                      }}
                    >
                      <FolderOpenIcon
                        sx={{ color: "#90b2b7", marginRight: ".5em" }}
                      />
                      {fecha}
                    </div>
                  }
                  collapsed={!expandedNodes[`${userId}-${fecha}`]}
                >
                  {maletas.map((maleta) => (
                    <div
                      key={maleta.id}
                      style={{
                        display: "flex",
                        cursor: "pointer",
                        margin: ".5em 0",
                        marginLeft: ".7em",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          marginLeft: ".7em",
                        }}
                        /* onClick={() => setSelectedMaleta(maleta)} */
                        onClick={() => handleSelectMaleta(maleta)}
                      >
                        <WorkOutlineOutlinedIcon
                          sx={{ marginRight: "8px", color: "#FFAC7E" }}
                        />
                        {maleta.nombre}
                      </Box>

                      {/* Botón Editar */}

                      <IconButton
                        onClick={() => handleEditMaleta(maleta)}
                        sx={{
                          ml: 3,
                          border: "1px solid #ffac7e",
                          backgroundColor: theme.palette.background.paper,
                          color: "#ffac7e",
                          padding: "5px",
                          "&:hover": {
                            backgroundColor: "#90b2b7",
                            color: "#fff",
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </div>
                  ))}
                </TreeView>
              ))}
            </TreeView>
          ))
        ) : (
          <Typography variant="body2">No hay maletas registradas</Typography>
        )}
      </Box>

      {selectedMaleta && (
        <Box
          sx={{
            flex: 1,
            mr: 2,
            p: 2,
            backgroundColor: theme.palette.background.paper,
            borderRadius: "4px",
            "&:hover": { border: "1px solid #000" },
          }}
        >
          <Typography variant="h6">Maleta: {selectedMaleta.nombre}</Typography>
          {selectedMaleta.muestras && selectedMaleta.muestras.length > 0 ? (
            <List>
              {selectedMaleta.muestras.map((muestra) => (
                <ListItem key={muestra.ID_MUESTRA}>
                  <ListItemText primary={muestra.MUESTRA} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" sx={{ mt: 2, color: "gray" }}>
              No hay muestras en esta maleta.
            </Typography>
          )}
        </Box>
      )}
      {/* Drawer para crear nueva maleta */}
      <DrawerNuevaMaleta
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onMaletaCreated={fetchMaletas}
      />

      {/* Drawer para editar maleta */}
      {selectedMaleta && (
        <DrawerEditarMaleta
          open={isEditDrawerOpen}
          onClose={() => setIsEditDrawerOpen(false)}
          maletaId={selectedMaleta.id}
          maleta={selectedMaleta}
          onMuestrasUpdated={fetchMaletas}
          onMuestraAdded={handleMuestraAdded} // ✅ Se actualiza Maletas.js tras añadir muestra
          onMuestraRemoved={handleMuestraRemoved} // 🔹 Ahora escucha la eliminación
        />
      )}
    </Box>
  );
};

export default Maletas;
